import { z } from "zod";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
import { booleanQueryParam } from "../common/query-params.js";
import { webhookEventSlug } from "./webhooks.events.js";
// Custom headers: a flat string→string map, capped and validated. Names must be
// RFC 7230 tokens; names and values reject CR/LF/control chars (otherwise the
// Node HTTP layer throws at delivery time and every delivery silently fails).
// Reserved headers (Content-Type, X-OnShelf-*) can't be overridden — they'd be
// dropped by the signer anyway, so reject them up front with a clear error.
const HEADER_NAME_RE = /^[A-Za-z0-9!#$%&'*+\-.^_`|~]+$/;
const HEADER_CTRL_RE = /[\x00-\x08\x0A-\x1F\x7F]/; // control chars except TAB (rejects CR/LF/NUL/DEL)
const headerKeySchema = z
    .string()
    .min(1)
    .max(128)
    .regex(HEADER_NAME_RE, "invalid header name — use letters, digits, or !#$%&'*+-.^_`|~");
const headerValueSchema = z
    .string()
    .max(1024)
    .refine((v) => !HEADER_CTRL_RE.test(v), "header value must not contain control characters");
const headersSchema = z
    .record(headerKeySchema, headerValueSchema)
    .refine((h) => Object.keys(h).length <= 20, "at most 20 headers")
    .refine((h) => !Object.keys(h).some((k) => k.toLowerCase() === "content-type" || k.toLowerCase().startsWith("x-onshelf-")), "Content-Type and X-OnShelf-* headers are reserved and can't be set")
    .default({});
export const webhookInfo = z.object({
    id: z.string().uuid(),
    eventSlug: z.string(),
    url: z.string().url(),
    description: z.string().nullable(),
    isActive: z.boolean(),
    headers: z.record(z.string()),
    failureCount: z.number().int(),
    disabledAt: z.string().datetime().nullable(),
    lastDeliveryAt: z.string().datetime().nullable(),
    lastStatus: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createWebhookRequest = z.object({
    eventSlugs: z.array(webhookEventSlug).min(1),
    url: z.string().url(),
    description: z.string().max(500).optional(),
    isActive: z.boolean().optional().default(true),
    headers: headersSchema.optional(),
    secret: z.string().min(16).max(256).optional(),
});
export const updateWebhookRequest = z
    .object({
    url: z.string().url(),
    description: z.string().max(500).nullable(),
    isActive: z.boolean(),
    headers: headersSchema,
})
    .partial();
export const rotateSecretRequest = z.object({
    secret: z.string().min(16).max(256).optional(),
});
// Pre-save reachability probe for the New-webhook form.
export const testWebhookUrlRequest = z.object({ url: z.string().url() });
export const testWebhookUrlResponse = z.object({
    reachable: z.boolean(),
    status: z.number().int().nullable(),
    error: z.string().nullable(),
});
export const secretRevealResponse = z.object({
    webhooks: z.array(webhookInfo),
    secret: z.string(),
});
export const webhookDeliveryInfo = z.object({
    id: z.string().uuid(),
    webhookId: z.string().uuid(),
    eventId: z.string().uuid(),
    eventSlug: z.string(),
    status: z.enum(["pending", "success", "failed"]),
    attemptCount: z.number().int(),
    responseStatus: z.number().int().nullable(),
    responseBodySnippet: z.string().nullable(),
    lastError: z.string().nullable(),
    /** Latency of the last attempt's request round-trip, in ms. Null for
     *  not-yet-attempted deliveries and rows predating this measurement. */
    responseDurationMs: z.number().int().nullable(),
    deliveredAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
/** A single delivery, expanded with the request that was sent (reconstructed
 *  from the stored event payload + webhook config; custom header values are
 *  masked). The response side reuses responseStatus/responseBodySnippet/
 *  lastError above. */
export const webhookDeliveryDetail = webhookDeliveryInfo.extend({
    /** The JSON envelope body POSTed to the endpoint. */
    requestBody: z.record(z.unknown()),
    /** The request headers sent (custom header VALUES masked). */
    requestHeaders: z.record(z.string()),
});
export const webhookEventTypeInfo = z.object({
    slug: z.string(),
    name: z.string(),
    description: z.string(),
    category: z.string(),
    sampleData: z.record(z.unknown()).nullable(),
});
export const webhookEventTypesResponse = z.object({
    items: z.array(webhookEventTypeInfo),
});
export const eventEnvelope = z.object({
    id: z.string().uuid(),
    type: z.string(),
    apiVersion: z.literal("v2"),
    occurredAt: z.string().datetime(),
    tenantId: z.string().uuid(),
    data: z.record(z.unknown()),
});
export const listWebhooksQuery = paginationQuerySchema.extend({
    eventSlug: z.string().optional(),
    isActive: booleanQueryParam.optional(),
});
export const webhooksListResponse = pagedResponse(webhookInfo);
export const listDeliveriesQuery = paginationQuerySchema.extend({
    status: z.enum(["pending", "success", "failed"]).optional(),
});
export const deliveriesListResponse = pagedResponse(webhookDeliveryInfo);
//# sourceMappingURL=webhooks.schemas.js.map